import { create } from "zustand";
import { ApiError, ListingCardData } from "@/types/types";
import { toast } from "react-toastify";
import { Location, LocationsResponse } from "./types";
import { env } from "@/lib/env";

const ADMINENPOINTURL = env.NEXT_ADMIN_API_URL as string;
const PROFILESENPOINTURL = env.NEXT_PROFILES_API_URL as string;
const LOCATIONSPHOTOSDISTRIBUTIONURL =
  env.NEXT_PUBLIC_LOCATIONS_PHOTOS_DISTRIBUTION_URL as string;

interface LocationStore {
  locations: Location[];
  count: number;
  current_page_number: number;
  fetch_with_photos: string;
  initStore: (locationsData: LocationsResponse) => void;
  setFetchWithPhotos: (
    value: string,
    pageNumber: number,
    idToken: string | null,
    locale: string
  ) => Promise<void>;
  getPage: (
    pageNumber: number,
    idToken: string | null,
    locale: string
  ) => Promise<void>;
  uploadPhoto: (
    photo: File,
    location: Location,
    idToken: string | null
  ) => Promise<void>;
  deletePhotoUrl: (location: Location, idToken: string | null) => Promise<void>;
  upgradeToCity: (
    location: Location,
    newVal: boolean,
    idToken: string | null
  ) => Promise<void>;
}

export const useLocationsStore = create<LocationStore>((set, get) => ({
  locations: [],
  count: 0,
  current_page_number: 1,
  fetch_with_photos: "",
  setFetchWithPhotos: async (value, pageNumber, idToken, locale) => {
    set({ fetch_with_photos: value });
    await get().getPage(pageNumber, idToken, locale);
  },
  initStore: (locationsData) => {
    set({
      locations: locationsData.locations,
      count: locationsData.count,
      current_page_number: locationsData.page_number,
    });
  },
  getPage: async (pageNumber, idToken, locale) => {
    if (idToken === null) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    try {
      const fetch_with_photos = get().fetch_with_photos;
      const response = await fetch(
        `${ADMINENPOINTURL}/getAvailableLocations?has_photo=${fetch_with_photos}&page_number=${pageNumber}&has_page_count=false&locale=${locale}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: idToken,
          },
        }
      );
      if (response.ok) {
        const locationsData = (await response.json()) as LocationsResponse;
        set((state) => ({
          locations: locationsData.locations,
          count: locationsData.count ?? state.count,
          current_page_number: locationsData.page_number,
        }));
      } else {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to get locations data."
          : errorMessage,
        { type: "error" }
      );
    }
  },
  uploadPhoto: async (photo, location, idToken) => {
    if (idToken === null) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    try {
      const cleanSlug = location.slug.replaceAll("#", "/");
      const response = await fetch(
        `${PROFILESENPOINTURL}/generateS3UploadLinks`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: idToken,
          },
          body: JSON.stringify({
            image_keys: [photo.name],
            folder_id: cleanSlug,
            root_folder_id: location.type === "city" ? "cities/" : "countries/",
            bucket_name: env.NEXT_PUBLIC_LOCATIONS_PHOTOS_BUCKET_NAME,
          }),
        }
      );
      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      const urls = (await response.json()) as URLS;
      await uploadPhoto(photo, urls.urls[photo.name]);
      const distributionUrl = `${LOCATIONSPHOTOSDISTRIBUTIONURL}/${
        location.type === "city" ? "cities/" : "countries/"
      }${cleanSlug}/${photo.name}`;
      const setUrlReponse = await fetch(
        `${ADMINENPOINTURL}/addPhotoUrlForLocation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: idToken,
          },
          body: JSON.stringify({
            slug: location.slug,
            photo_url: distributionUrl,
          }),
          cache: "no-cache",
        }
      );
      if (setUrlReponse.ok) {
        set((state) => ({
          locations: state.locations.map((stored_location) =>
            stored_location.slug === location.slug
              ? {
                  ...stored_location,
                  photo_url: distributionUrl,
                  has_photo: true,
                }
              : stored_location
          ),
        }));
      } else {
        const apiErr = (await setUrlReponse.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      return;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to upload location photo."
          : errorMessage,
        {
          type: "error",
        }
      );
    }
  },
  deletePhotoUrl: async (location, idToken) => {
    if (idToken === null) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    try {
      const setUrlReponse = await fetch(
        `${ADMINENPOINTURL}/deletePhotoUrlForLocation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: idToken,
          },
          body: JSON.stringify({
            slug: location.slug,
          }),
          cache: "no-cache",
        }
      );
      if (setUrlReponse.ok) {
        set((state) => ({
          locations: state.locations.map((stored_location) =>
            stored_location.slug === location.slug
              ? { ...stored_location, photo_url: "", has_photo: false }
              : stored_location
          ),
        }));
      } else {
        const apiErr = (await setUrlReponse.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      return;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to delete location photo."
          : errorMessage,
        {
          type: "error",
        }
      );
    }
  },
  upgradeToCity: async (location, newVal, idToken) => {
    if (idToken === null) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    try {
      const setUrlReponse = await fetch(
        `${ADMINENPOINTURL}/upgradeTreatAsCity`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: idToken,
          },
          body: JSON.stringify({
            slug: location.slug,
            value: newVal,
          }),
          cache: "no-cache",
        }
      );
      if (setUrlReponse.ok) {
        set((state) => ({
          locations: state.locations.map((stored_location) =>
            stored_location.slug === location.slug
              ? { ...stored_location, treat_as_city: newVal }
              : stored_location
          ),
        }));
      } else {
        const apiErr = (await setUrlReponse.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      return;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to upgrade to city."
          : errorMessage,
        {
          type: "error",
        }
      );
    }
  },
}));

const uploadPhoto = async (photoData: File, url: string) => {
  const uploadResponse = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": photoData.type,
    },
    body: photoData,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed for ${photoData.name}`);
  }

  return uploadResponse;
};

interface URLS {
  urls: { [key: string]: string };
}
